import React, { useState, useEffect, useMemo } from "react";
import api from "../services/api";
import {
  SearchIcon
} from "./Icons";

function CategoryPickerModal({
  isOpen,
  onClose,
  onSelectCategory,
  initialCategoryId = null
}) {
  const [tree, setTree] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedL1, setSelectedL1] = useState(null);
  const [selectedL2, setSelectedL2] = useState(null);
  const [selectedL3, setSelectedL3] = useState(null);

  // Custom request drawer state
  const [showCustomRequest, setShowCustomRequest] = useState(false);
  const [customProdName, setCustomProdName] = useState("");
  const [customCatName, setCustomCatName] = useState("");
  const [customSubCat, setCustomSubCat] = useState("");
  const [customProdType, setCustomProdType] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchTree();
    }
  }, [isOpen]);

  const fetchTree = async () => {
    try {
      const res = await api.get("/categories/tree");
      setTree(res.data.tree || []);
    } catch (err) {
      console.error("Failed to load category tree:", err);
    }
  };

  // Search results across full tree
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.trim().toLowerCase();
    const results = [];

    tree.forEach((l1) => {
      const l1Matches = l1.name.toLowerCase().includes(q) || (l1.keywords || []).some(k => k.toLowerCase().includes(q));
      if (l1Matches && (!l1.children || l1.children.length === 0)) {
        results.push({ l1, l2: null, l3: null, label: l1.name });
      }

      (l1.children || []).forEach((l2) => {
        const l2Matches = l2.name.toLowerCase().includes(q) || (l2.keywords || []).some(k => k.toLowerCase().includes(q));
        if (l2Matches && (!l2.children || l2.children.length === 0)) {
          results.push({ l1, l2, l3: null, label: `${l1.name} → ${l2.name}` });
        }

        (l2.children || []).forEach((l3) => {
          const l3Matches = l3.name.toLowerCase().includes(q) || (l3.keywords || []).some(k => k.toLowerCase().includes(q));
          if (l3Matches || l2Matches || l1Matches) {
            results.push({ l1, l2, l3, label: `${l1.name} → ${l2.name} → ${l3.name}` });
          }
        });
      });
    });

    return results.slice(0, 30);
  }, [tree, searchQuery]);

  const handleConfirm = () => {
    if (!selectedL1) return;

    const chosen = selectedL3 || selectedL2 || selectedL1;
    onSelectCategory({
      categoryId: selectedL1._id,
      categoryName: selectedL1.name,
      subcategoryId: selectedL2 ? selectedL2._id : null,
      subcategoryName: selectedL2 ? selectedL2.name : "",
      productTypeId: selectedL3 ? selectedL3._id : null,
      productTypeName: selectedL3 ? selectedL3.name : "",
      fullPath: [selectedL1.name, selectedL2?.name, selectedL3?.name].filter(Boolean).join(" → "),
      attributes: selectedL1.attributes || [],
      tags: chosen.tags || selectedL1.tags || []
    });
    onClose();
  };

  const handleSelectSearchResult = (res) => {
    setSelectedL1(res.l1);
    setSelectedL2(res.l2);
    setSelectedL3(res.l3);
    setSearchQuery("");
  };

  const handleSubmitCustomRequest = async (e) => {
    e.preventDefault();
    setSubmittingRequest(true);
    setRequestSuccess("");
    try {
      const res = await api.post("/vendor/category-requests", {
        productName: customProdName,
        suggestedCategory: customCatName,
        suggestedSubcategory: customSubCat,
        suggestedProductType: customProdType,
        description: customDesc
      });

      // Find or fallback to "Other" category
      const otherCat = tree.find(c => c.name.toLowerCase() === 'other') || {
        _id: null,
        name: "Other"
      };

      onSelectCategory({
        categoryId: otherCat._id,
        categoryName: "Other",
        subcategoryId: null,
        subcategoryName: customSubCat,
        productTypeId: null,
        productTypeName: customProdType,
        fullPath: `Other → ${customCatName}${customSubCat ? ` → ${customSubCat}` : ''}`,
        customCategoryRequestId: res.data.request?._id,
        attributes: [],
        tags: ["Other", "Custom"]
      });

      setRequestSuccess("Request submitted successfully!");
      setTimeout(() => {
        onClose();
        setShowCustomRequest(false);
      }, 1000);
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to submit request.");
    } finally {
      setSubmittingRequest(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(17, 19, 21, 0.65)",
        backdropFilter: "blur(4px)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px"
      }}
    >
      <div
        className="category-picker-modal-box"
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "960px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          overflow: "hidden"
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid #edf0f4",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <div>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#111315", margin: 0 }}>
              Where Does Your Product Belong?
            </h3>
            <p style={{ fontSize: "12.5px", color: "var(--text-muted)", margin: "2px 0 0" }}>
              Select the most accurate category so local buyers can discover your items easily.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#f0f2f5",
              border: "none",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "700",
              color: "#555"
            }}
          >
            ✕
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ padding: "14px 24px", background: "#fbfaf8", borderBottom: "1px solid #edf0f4" }}>
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", left: "12px", top: "10px", color: "var(--text-muted)" }}>
              <SearchIcon size={16} />
            </div>
            <input
              type="text"
              className="form-control"
              style={{ paddingLeft: "38px", fontSize: "13.5px" }}
              placeholder="Search category, subcategory or product type (e.g. 'jeans', 'running shoes', 'smartphones')..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Body Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          {showCustomRequest ? (
            /* Custom Category Request Form */
            <form onSubmit={handleSubmitCustomRequest}>
              <div style={{ background: "#fff5f4", border: "1px solid #fecaca", borderRadius: "10px", padding: "14px", marginBottom: "16px" }}>
                <strong style={{ fontSize: "13.5px", color: "var(--primary)", display: "block" }}>
                  💡 Can&apos;t find your exact category?
                </strong>
                <p style={{ fontSize: "12px", color: "var(--text-mid)", margin: "2px 0 0" }}>
                  Tell us what you are selling. Your product will be listed under <strong>Other</strong> immediately so you can sell right away, while our Admin reviews your category suggestion.
                </p>
              </div>

              {requestSuccess && (
                <div style={{ background: "#dcfce7", color: "#166534", padding: "10px 14px", borderRadius: "8px", marginBottom: "14px", fontSize: "13px", fontWeight: "700" }}>
                  ✓ {requestSuccess}
                </div>
              )}

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Product Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={customProdName}
                    onChange={(e) => setCustomProdName(e.target.value)}
                    placeholder="e.g. Traditional Wooden Carved Elephant"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Suggested Main Category *</label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={customCatName}
                    onChange={(e) => setCustomCatName(e.target.value)}
                    placeholder="e.g. Handmade & Crafts"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Suggested Subcategory</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customSubCat}
                    onChange={(e) => setCustomSubCat(e.target.value)}
                    placeholder="e.g. Wooden Handicrafts"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Suggested Product Type</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customProdType}
                    onChange={(e) => setCustomProdType(e.target.value)}
                    placeholder="e.g. Sculptures & Figurines"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Brief Description of Product</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={customDesc}
                    onChange={(e) => setCustomDesc(e.target.value)}
                    placeholder="Describe how buyers use or wear this product..."
                  />
                </div>
              </div>

              <div style={{ marginTop: "18px", display: "flex", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => setShowCustomRequest(false)}
                  className="btn btn-outline-secondary"
                  style={{ fontSize: "12.5px" }}
                >
                  &larr; Back to Category Browser
                </button>
                <button
                  type="submit"
                  disabled={submittingRequest}
                  className="btn btn-primary"
                  style={{ fontSize: "12.5px" }}
                >
                  {submittingRequest ? "Submitting..." : "Submit & List Under Other"}
                </button>
              </div>
            </form>
          ) : searchQuery.trim() ? (
            /* Search Results View */
            <div>
              <div style={{ fontSize: "12px", fontWeight: "800", color: "var(--text-muted)", marginBottom: "10px", textTransform: "uppercase" }}>
                Search Results ({searchResults.length})
              </div>
              {searchResults.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {searchResults.map((res, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSelectSearchResult(res)}
                      style={{
                        textAlign: "left",
                        background: "#fbfaf8",
                        border: "1px solid #edf0f4",
                        borderRadius: "8px",
                        padding: "10px 14px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "all 0.15s ease"
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--primary)")}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#edf0f4")}
                    >
                      <div>
                        <strong style={{ fontSize: "13.5px", color: "#111315" }}>{res.label}</strong>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block" }}>
                          {res.l1.name} {res.l2 ? `› ${res.l2.name}` : ''} {res.l3 ? `› ${res.l3.name}` : ''}
                        </span>
                      </div>
                      <span style={{ fontSize: "12px", color: "var(--primary)", fontWeight: "800" }}>Select &rarr;</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "30px 20px" }}>
                  <p style={{ fontSize: "13.5px", color: "var(--text-muted)", marginBottom: "12px" }}>
                    No matching categories found for &ldquo;{searchQuery}&rdquo;.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomProdName(searchQuery);
                      setShowCustomRequest(true);
                    }}
                    className="btn btn-outline-primary"
                    style={{ fontSize: "12.5px" }}
                  >
                    + Request &ldquo;{searchQuery}&rdquo; as Custom Category
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* 3-Column Hierarchy Explorer */
            <div className="category-picker-columns" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px", minHeight: "340px" }}>
              {/* Column 1: Level 1 Categories */}
              <div style={{ borderRight: "1px solid #edf0f4", paddingRight: "10px", maxHeight: "380px", overflowY: "auto" }}>
                <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "8px" }}>
                  1. Major Category
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {tree.map((cat) => {
                    const isSelected = selectedL1?._id === cat._id;
                    return (
                      <div
                        key={cat._id}
                        onClick={() => {
                          setSelectedL1(cat);
                          setSelectedL2(null);
                          setSelectedL3(null);
                        }}
                        style={{
                          padding: "8px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          background: isSelected ? "var(--primary-light)" : "transparent",
                          color: isSelected ? "var(--primary)" : "#111315",
                          fontWeight: isSelected ? "800" : "500",
                          fontSize: "13px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between"
                        }}
                      >
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cat.name}</span>
                        {cat.children?.length > 0 && <span style={{ opacity: 0.5, fontSize: "11px" }}>&rsaquo;</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Column 2: Level 2 Subcategories */}
              <div style={{ borderRight: "1px solid #edf0f4", paddingRight: "10px", maxHeight: "380px", overflowY: "auto" }}>
                <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "8px" }}>
                  2. Subcategory
                </div>
                {selectedL1 ? (
                  selectedL1.children?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      {selectedL1.children.map((sub) => {
                        const isSelected = selectedL2?._id === sub._id;
                        return (
                          <div
                            key={sub._id}
                            onClick={() => {
                              setSelectedL2(sub);
                              setSelectedL3(null);
                            }}
                            style={{
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              background: isSelected ? "var(--primary-light)" : "transparent",
                              color: isSelected ? "var(--primary)" : "#111315",
                              fontWeight: isSelected ? "800" : "500",
                              fontSize: "13px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between"
                            }}
                          >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub.name}</span>
                            {sub.children?.length > 0 && <span style={{ opacity: 0.5, fontSize: "11px" }}>&rsaquo;</span>}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ color: "var(--text-muted)", fontSize: "12px", padding: "10px 0" }}>
                      No subcategories needed for {selectedL1.name}. You can proceed with this category.
                    </div>
                  )
                ) : (
                  <div style={{ color: "var(--text-muted)", fontSize: "12px", padding: "10px 0" }}>
                    Select a major category on the left.
                  </div>
                )}
              </div>

              {/* Column 3: Level 3 Product Types */}
              <div style={{ maxHeight: "380px", overflowY: "auto" }}>
                <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "8px" }}>
                  3. Product Type
                </div>
                {selectedL2 ? (
                  selectedL2.children?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      {selectedL2.children.map((pt) => {
                        const isSelected = selectedL3?._id === pt._id;
                        return (
                          <div
                            key={pt._id}
                            onClick={() => setSelectedL3(pt)}
                            style={{
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              background: isSelected ? "var(--primary-light)" : "transparent",
                              color: isSelected ? "var(--primary)" : "#111315",
                              fontWeight: isSelected ? "800" : "500",
                              fontSize: "13px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between"
                            }}
                          >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pt.name}</span>
                            {isSelected && <span style={{ color: "var(--primary)", fontWeight: "900" }}>✓</span>}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ color: "var(--text-muted)", fontSize: "12px", padding: "10px 0" }}>
                      No specific product types. Subcategory {selectedL2.name} is ready.
                    </div>
                  )
                ) : (
                  <div style={{ color: "var(--text-muted)", fontSize: "12px", padding: "10px 0" }}>
                    Select a subcategory in the middle column.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer & Selection Confirmation Bar */}
        <div
          style={{
            padding: "16px 24px",
            background: "#fbfaf8",
            borderTop: "1px solid #edf0f4",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px"
          }}
        >
          {/* Breadcrumb Path Preview */}
          <div style={{ flex: 1, minWidth: "260px" }}>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: "700", textTransform: "uppercase", display: "block" }}>
              CURRENT SELECTION
            </span>
            <div style={{ fontSize: "13.5px", fontWeight: "800", color: selectedL1 ? "#111315" : "var(--text-muted)" }}>
              {selectedL1 ? (
                <span>
                  {selectedL1.name}
                  {selectedL2 && <span> &rsaquo; {selectedL2.name}</span>}
                  {selectedL3 && <span style={{ color: "var(--primary)" }}> &rsaquo; {selectedL3.name}</span>}
                </span>
              ) : (
                "None selected yet"
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {!showCustomRequest && (
              <button
                type="button"
                onClick={() => setShowCustomRequest(true)}
                className="btn btn-outline-secondary"
                style={{ fontSize: "12px", padding: "7px 14px" }}
              >
                Can&apos;t find category?
              </button>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedL1}
              className="btn btn-primary"
              style={{ fontSize: "12.5px", padding: "8px 20px", fontWeight: "800" }}
            >
              Confirm Category &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CategoryPickerModal;
